import { useState, useMemo } from "react";
import { type DateRange } from "react-day-picker";
import { subDays, differenceInDays, addDays } from "date-fns";
import { IconInfoCircle } from "@tabler/icons-react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { PageContent } from "@/components/PageContent";
import { DashboardCard, TotalSpend, LastXDaysSpend, ProvidersBreakdown, HorizontalBarChart, UsageChart, SpendChart} from "@/components/Dashboard";
import { BiDollarCircle } from "react-icons/bi";
import { TbCalendarDollar } from "react-icons/tb";
import { MdQueryStats } from "react-icons/md";
import { RiDonutChartFill } from "react-icons/ri";
import { MdOutlineIntegrationInstructions } from "react-icons/md";
import { useOverviewTimeSeries, useOverviewTotalSpend, useOverviewProvidersBreakdown, useOverviewContributorsSpend, useOverviewSpend } from "./logic";
import { useAuth } from "@/hooks/useAuth";
import { UpgradeBanner } from "@/components/UpgradeBanner";

const MAX_CHART_DATE_RANGE_DAYS = 90;

export function OverviewPage() {
  const { isOnFreePlan } = useAuth();

  // Date range state for API requests
  const [appliedDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 29),
    to: new Date(),
  });

  // Models state - default to all models
  // Note: Overview page doesn't have a model selector, so it always uses all models

  // Calculate the actual days in selected range
  const selectedDays = useMemo(() => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return 0;
    return differenceInDays(appliedDateRange.to, appliedDateRange.from) + 1;
  }, [appliedDateRange]);

  // Check if the date range exceeds the chart limit
  const isChartDateRangeCapped = selectedDays > MAX_CHART_DATE_RANGE_DAYS;

  // Calculate the capped date range for the chart component
  const chartDateRange = useMemo((): DateRange | undefined => {
    if (!appliedDateRange?.from || !appliedDateRange?.to) return appliedDateRange;
    
    if (isChartDateRangeCapped) {
      return {
        from: appliedDateRange.from,
        to: addDays(appliedDateRange.from, MAX_CHART_DATE_RANGE_DAYS - 1)
      };
    }
    return appliedDateRange;
  }, [appliedDateRange, isChartDateRangeCapped]);

  // Calculate days displayed in the chart
  const chartDisplayDays = isChartDateRangeCapped ? MAX_CHART_DATE_RANGE_DAYS : selectedDays;

  const { timeSeriesData, isLoading, granularity } = useOverviewTimeSeries(chartDateRange);
  const { totalSpendData, isLoadingTotalSpend } = useOverviewTotalSpend(appliedDateRange);
  const { providersBreakdownData, isLoadingProvidersBreakdown } = useOverviewProvidersBreakdown(appliedDateRange);
  const { contributorsSpendData, isLoadingContributorsSpend } = useOverviewContributorsSpend(appliedDateRange);
  const { spendData, isLoadingSpend } = useOverviewSpend(appliedDateRange);

  // Dynamic title for the Last X Days Spend card
  const lastXDaysTitle = (
    <span className="flex items-center gap-1.5">
      Last {chartDisplayDays} Days Spend
      {isChartDateRangeCapped && (
        <TooltipPrimitive.Provider delayDuration={200}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <span className="inline-flex">
                <IconInfoCircle className="size-4 text-[#8A8F98]" />
              </span>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                side="top"
                align="center"
                sideOffset={6}
                className="z-50 min-w-[160px] max-w-[280px] bg-white border border-[#EDEEF3] rounded-[8px] shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)] p-[12px] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 origin-[var(--radix-tooltip-content-transform-origin)]"
              >
                <p className="text-[12px] text-[#08090A] leading-[18px] m-0">
                  Chart limited to {MAX_CHART_DATE_RANGE_DAYS} days. You selected {selectedDays} days.
                </p>
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
      )}
    </span>
  );

  return (
    <div>
      {isOnFreePlan && (
        <UpgradeBanner type="data-retention" />
      )}
      <PageContent>
        <div className="space-y-4">
          {/* Row 1 */}
          <div className="grid grid-cols-[3fr_4fr] gap-4">
            <DashboardCard
              icon={<BiDollarCircle />}
              title="Total Spend"
              subtitle="Your cumulative AI spending across all providers and models. Track this to understand your overall budget consumption and identify spending trends over time."
            >
              <TotalSpend data={totalSpendData} isLoading={isLoadingTotalSpend} />
            </DashboardCard>
            <DashboardCard
              icon={<TbCalendarDollar />}
              title={lastXDaysTitle}
              subtitle="A breakdown of your AI costs over the selected period by modality (text, image, audio, video). Use this to spot daily spending patterns and identify which modalities are driving costs."
            >
              <LastXDaysSpend
                data={timeSeriesData}
                granularity={granularity}
                dateRange={chartDateRange}
                isLoading={isLoading}
              />
            </DashboardCard>
          </div>
        <div className="grid grid-cols-[3fr_3fr] gap-4">
          <DashboardCard
            icon={<RiDonutChartFill />}
            title="Top Contributors to AI Spend"
            subtitle="Identify which teams, projects, or users are generating the most AI costs. Essential for cost allocation, budget planning, and ensuring resources are being used efficiently across your organization."
          >
            <HorizontalBarChart data={contributorsSpendData} isLoading={isLoadingContributorsSpend} />
          </DashboardCard>
          <DashboardCard
            icon={<MdOutlineIntegrationInstructions />}
            title="Providers Breakdown"
            subtitle="Compare costs across AI providers (OpenAI, Anthropic, Google, etc.) to optimize your provider mix. Track average costs and usage trends to negotiate better rates or shift workloads to more cost-effective alternatives."
          >
            <ProvidersBreakdown data={providersBreakdownData} isLoading={isLoadingProvidersBreakdown} />
          </DashboardCard>
        </div>
          {/* Row 2 */}
          <div className="grid grid-cols-[1fr_1fr] gap-4">
          <DashboardCard
            icon={<MdQueryStats />}
            title="Usage"
            subtitle="Monitor total events across providers to track usage patterns and identify trends. Deviations in usage growth may indicate changes in product adoption, user behavior, or integration issues that should be reviewed."
          >
            <UsageChart data={spendData} isLoading={isLoadingSpend} />
          </DashboardCard>
          <DashboardCard
            icon={<MdQueryStats />}
            title="Spend"
            subtitle="Track average cost per action to understand spending efficiency. Costs should scale proportionally with usage. Faster cost growth, gradual upward drift, or sudden spikes may point to inefficiencies or model changes that need attention."
          >
            <SpendChart data={spendData} isLoading={isLoadingSpend} />
          </DashboardCard>
        </div>
        </div>
      </PageContent>
    </div>
  )
}
